import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import { articles, categories, latestNews } from '@/mocks/games';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = useMemo(() => {
    const found = articles.find((a) => a.slug === slug);
    if (found) return found;
    return latestNews.find((n) => n.slug === slug);
  }, [slug]);

  const categoryInfo = article
    ? categories.find((c) => c.slug === article.category)
    : null;

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((a) => a.slug !== article.slug && a.category === article.category)
      .slice(0, 3);
  }, [article]);

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-3">
              文章未找到
            </h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 cursor-pointer">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const seoData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: { '@type': 'Person', name: article.author },
    datePublished: article.date,
    inLanguage: 'zh-CN',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://eroge-guide.com/article/${slug}`,
    },
  };

  const categoryName = categoryInfo
    ? categoryInfo.name.split(' ')[0]
    : article.category.toUpperCase();

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${article.title} - 工口游戏攻略大全`}
        description={article.excerpt}
        keywords={`工口游戏,${article.title},工口游戏攻略,${categoryName},工口游戏推荐`}
        canonicalUrl={`https://eroge-guide.com/article/${slug}`}
        schemaJson={seoData}
      />
      <Navbar />

      <main className="pt-20">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              ...(categoryInfo
                ? [{ label: categoryInfo.name, href: `/category/${article.category}` }]
                : []),
              { label: article.title },
            ]}
          />

          <article className="pb-14">
            {/* Header */}
            <header className="mb-8">
              <div className="flex items-center gap-2 flex-wrap mb-4">
                <span className="text-xs px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 font-medium">
                  {categoryName}
                </span>
                {'rating' in article && article.rating && (
                  <span className="flex items-center gap-1 text-sm text-foreground-600">
                    <i className="ri-star-fill text-accent-400"></i>
                    {article.rating}
                  </span>
                )}
              </div>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
                {'readTime' in article && (
                  <span className="flex items-center gap-1.5">
                    <i className="ri-time-line"></i>
                    {article.readTime}
                  </span>
                )}
              </div>
            </header>

            {/* Featured Image */}
            <div className="relative w-full h-64 md:h-96 rounded-lg overflow-hidden mb-8">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            {/* Content */}
            <div
              className="prose-custom text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{
                __html:
                  'content' in article
                    ? article.content
                    : `<h2>${article.title}</h2><p>${article.excerpt}</p><p>更多详细内容即将更新，敬请期待。欢迎持续关注工口游戏攻略大全，获取最新最全的工口游戏资讯与攻略。</p>`,
              }}
            />

            {/* Tags */}
            <div className="mt-10 pt-6 border-t border-background-200/60">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-foreground-500">标签：</span>
                <span className="text-xs px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700">
                  工口游戏
                </span>
                <span className="text-xs px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700">
                  {categoryName}
                </span>
                <span className="text-xs px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700">
                  游戏攻略
                </span>
              </div>
            </div>
          </article>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="pb-14">
              <h3 className="text-lg font-heading font-bold text-foreground-900 mb-5">
                相关推荐
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {relatedArticles.map((rel) => (
                  <Link
                    key={rel.slug}
                    to={`/article/${rel.slug}`}
                    className="group block p-4 rounded-lg bg-background-50 border border-background-200/60 hover:border-primary-200/60 transition-all cursor-pointer"
                  >
                    <div className="relative w-full h-36 rounded-md overflow-hidden mb-3">
                      <img
                        src={rel.image}
                        alt={rel.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <h4 className="text-sm font-bold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {rel.title}
                    </h4>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}